import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faCalendar,
  faClock,
  faEllipsisVertical,
  faHourglassHalf,
  faLanguage,
  faLocationDot,
  faTreeCity,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { moreVerticalIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { ActionsResponse } from 'src/app/modules/dashboard/interface/actions-response';
import { IEPResponse, Mandate } from 'src/app/modules/dashboard/interface/dashboard-response';
import { StudentService } from 'src/app/services/student.service';
import { AuthStore } from 'src/app/stores/auth.store';
import { iepConstants } from '../../constants/constants';
import { User } from '../../models/user';
import { IEPService } from 'src/app/modules/iep/iep-document/services/iep.services';
import { CapatureFirstAttendResponse } from '../../interfaces/capture-first-attend';
import { ISedmsFormInput } from '../../interfaces/form/sedms-form-input.model';

@Component({
  selector: 'app-student-programs',
  templateUrl: './student-programs.component.html',
  styleUrls: ['./student-programs.component.scss'],
  standalone: false,
})
export class StudentProgramsComponent implements OnChanges, OnInit {
  @ViewChild('anchor', { read: ElementRef }) public anchor: ElementRef;
  @Input() title: string;
  @Input() programs: Mandate[];
  @Input() studentIEP: IEPResponse;
  @Input() nycId: string;
  @Input() isDashboardIep?: boolean;
  @Input() isTwelveMonthDialog: boolean = false;
  @Input() showMandate: boolean = true;
  @Input() showFinalizedDate: boolean;

  @Input() studentProfile: any; // TODO: This should be typed to `StudentProfile`, but that interface needs to be updated with the RFD information from the backend before that can happen.
  showCaptureFirstAttendDate$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  showRfdDialog$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  showEditStudentProfileDialog: boolean;
  activePopupIndex: boolean = false;
  moreVerticalIcon: SVGIcon = moreVerticalIcon;
  actionsList: ActionsResponse;
  user: User;
  cpseUser: boolean = false;
  // Assume placements exist until the FAD API responds, to avoid a flash of a hidden action.
  hasFirstAttendPlacements: boolean = true;
  actions: string[] = ['Capture First Attend Date'];
  selectedProgram: Mandate = null;

  constructor(
    library: FaIconLibrary,
    private studentService: StudentService,
    private authStore: AuthStore,
    private iepService: IEPService<ISedmsFormInput>,
  ) {
    library.addIcons(
      faLanguage,
      faClock,
      faHourglassHalf,
      faUserGroup,
      faCalendar,
      faLocationDot,
      faEllipsisVertical,
      faTreeCity,
    );
  }

  ngOnInit(): void {
    this.authStore.userInfo$.subscribe((user: User) => {
      this.user = user;
      this.cpseUser = user?.isGlobalUser && user?.isSupervisor;
      this.updateProgramOptions();
    });
  }

  get canEdit(): boolean {
    return this.cpseUser;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.studentIEP?.currentValue?.nycid) {
      this.fetchFirstAttendPlacements(changes.studentIEP.currentValue.nycid);
    }
    if (
      changes.title ||
      changes.programs ||
      changes.studentIEP ||
      changes.studentProfile
    ) {
      this.updateProgramOptions();
    }
  }

  fetchFirstAttendPlacements(nycid: string): void {
    this.iepService
      .getCaptureFirstDateByNycId(nycid)
      .subscribe((res: CapatureFirstAttendResponse) => {
        this.hasFirstAttendPlacements = !!res?.placementAttendenceResponces?.length;
        this.updateProgramOptions();
      });
  }

  updateProgramOptions(): void {
    const hasRfd: boolean = this.studentProfile?.rfd;
    const showFad: boolean = this.allowCaptureFAD();

    this.actionsList = {
      kip: null,
      relatedService: null,
      referrals: null,
      iep: null,
      assessments: null,
      placements: null,
      student:
        // Only show the RFD action to CPSE users, as that is the only user type that should be able to create an RFD.
        hasRfd && this.cpseUser
          ? {
            stageDetails: [
              {
                actionId: 0,
                actionName: ['Enter Reason for Delay'],
                subActionDetails: [],
              },
            ],
            stageName: 'Student',
            stageSk: null,
          }
          : null,
      miscellaneous:
        // Only show Capture FAD for the following cases:
        // 1. The user is not a CPSE user and the Capture FAD button should be shown based on the other criteria in the `allowCaptureFAD` method.
        // 2. The user is a CPSE user, the Capture FAD button should be shown based on the other criteria in the `allowCaptureFAD` method,
        //  and there is no RFD associated with the student's profile (as Capture FAD should not be allowed if there is an RFD, per the business rules).
        !hasRfd && showFad
          ? {
            stageDetails: [
              {
                actionId: 0,
                actionName: ['Capture First Attend and Authorization Date'],
                subActionDetails: [],
              },
            ],
            stageName: 'Miscellaneous',
            stageSk: null,
          }
          : null,
    };
  }

  onRfdSaved(): void {
    this.studentService.refreshGridData(true);
  }

  displayRfdDialog(value: boolean): void {
    this.showRfdDialog$.next(value);
  }

  displayFAD(): void {
    this.showCaptureFirstAttendDate$.next(true);
  }

  openCaptureFirstAttendDateDialog(): void {
    this.showCaptureFirstAttendDate$.next(true);
    this.activePopupIndex = null;
  }

  onFADSaved(): void {
    this.studentService.refreshGridData(true);
  }

  closeCaptureFirstAttendDateDialog(): void {
    this.showCaptureFirstAttendDate$.next(false);
  }

  selectProgram(mandate: Mandate): void {
    this.selectedProgram = mandate;
  }

  openEditStudentProfileDialog(): void {
    this.showEditStudentProfileDialog = true;
  }
  closeEditStudentProfileDialog(): void {
    this.showEditStudentProfileDialog = false;
  }

  allowCaptureFAD(data?: Mandate): boolean {
   // console.log(data, "dd");
    if (data) {
      return (
        (this.title !== iepConstants.relatedServices &&
          this.title !== iepConstants.assistiveTechnology &&
          (data && data?.mandateDescription !== iepConstants.seitDescription
          ) ||
          this.studentIEP?.seitAgency) &&
        !this.isTwelveMonthDialog) && data?.isPlacementAddedForCurrentService

    } else {
    return ((
      this.title !== iepConstants.relatedServices &&
      this.title !== iepConstants.assistiveTechnology &&
      ((this.programs?.length &&
        this.programs?.find(
          (item: Mandate) =>
            item.mandateDescription !== iepConstants.seitDescription,
        )) ||
        this.studentIEP?.seitAgency) &&
      !this.isTwelveMonthDialog &&
      this.hasFirstAttendPlacements
    ) && this.studentIEP?.isPlacementAddedForCurrentService)

  }
  }

  shouldShowGroupSize(program: Mandate): boolean {
    return !!program?.groupSize &&
      program?.groupType?.trim()?.toLowerCase() === 'other';
  }

  isProgram(mandate: Mandate): boolean {
    return mandate.classroomsetting !== null;
  }
}
